import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDocFromServer, enableMultiTabIndexedDbPersistence } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import firebaseConfigJson from '../../firebase-applet-config.json';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || firebaseConfigJson.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || firebaseConfigJson.authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || firebaseConfigJson.projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || firebaseConfigJson.storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || firebaseConfigJson.messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || firebaseConfigJson.appId,
  firestoreDatabaseId: import.meta.env.VITE_FIREBASE_DATABASE_ID || firebaseConfigJson.firestoreDatabaseId,
};

export const app = initializeApp(config);
export const auth = getAuth(app);
export const db = getFirestore(app, config.firestoreDatabaseId && config.firestoreDatabaseId !== '(default)' ? config.firestoreDatabaseId : undefined);
export const storage = getStorage(app);

// Helper to detect if the browser's IndexedDB engine is responsive (not hung)
const isIndexedDBResponsive = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      resolve(false);
      return;
    }

    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        console.warn("IndexedDB is unresponsive (hung). Disabling Firestore offline persistence.");
        resolve(false);
      }
    }, 1000);

    try {
      const request = window.indexedDB.open('pwa_db_responsiveness_test', 1);

      request.onsuccess = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          const dbInstance = request.result;
          dbInstance.close();
          try {
            window.indexedDB.deleteDatabase('pwa_db_responsiveness_test');
          } catch (e) {}
          resolve(true);
        }
      };

      request.onerror = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          resolve(false);
        }
      };

      request.onblocked = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          resolve(false);
        }
      };
    } catch (e) {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        resolve(false);
      }
    }
  });
};

// Enable local offline persistence (IndexedDB cache) with multi-tab support safely
if (typeof window !== 'undefined') {
  isIndexedDBResponsive().then((responsive) => {
    if (responsive) {
      enableMultiTabIndexedDbPersistence(db).catch((err) => {
        if (err.code === 'failed-precondition') {
          console.warn("Firestore offline persistence failed: Multiple tabs open.");
        } else if (err.code === 'unimplemented') {
          console.warn("Firestore offline persistence failed: Browser lacks support.");
        }
      });
    } else {
      console.warn("Skipping Firestore offline persistence to prevent thread hang due to unresponsive IndexedDB.");
    }
  });
}

// Initialize Firebase Cloud Messaging and export it
import { getMessaging, isSupported } from 'firebase/messaging';

export let messaging: any = null;
const messagingPromise = isSupported().then((supported) => {
  if (supported) {
    messaging = getMessaging(app);
    return messaging;
  }
  return null;
});

export const getMessagingInstance = () => messagingPromise;

export const getCollectionPath = (colName: string) => colName;


